import { useState } from 'react'

import { usePersonaInsightsApi, useRunPersonaInsightApi } from '@/common/api/modules/persona'

/**
 * ペルソナ状況示唆の状態。
 *
 * ボタンは件数の横、結果はその下、と離れた場所に出るので、
 * **状態をここに切り出して両方から使う**。ページ側にロジックを置かない。
 *
 * コンポーネントと同じファイルに置くと Fast Refresh が効かなくなるため分けている。
 */

/** 生成の段取り。AIを待つ間に何をしているかを見せる */
export const INSIGHT_STEPS = ['集計しています', 'AIが解釈しています', '保存しています']

/** 「AIが解釈中」へ進める目安の時間。実際の進捗は取れないので見た目のためだけ */
const STEP2_AFTER_MS = 1500

export const usePersonaInsight = (siteId: number, days: number) => {
  const { data, isLoading, mutate } = usePersonaInsightsApi({ site_id: siteId })
  const [selected, setSelected] = useState<number | null>(null)
  const [step, setStep] = useState(0)
  const [error, setError] = useState<string | null>(null)

  const { trigger, isMutating } = useRunPersonaInsightApi({
    onSuccess: (res) => {
      setStep(0)
      if (res && res.ok === false) {
        // 失敗は世代にならない。理由をそのまま出す（空の示唆を出さない）
        setError(res.error ?? '示唆を作れませんでした')
        return
      }
      setError(null)
      setSelected(res?.insight?.id ?? null)
      mutate()
    },
    onError: (e) => {
      setStep(0)
      setError(e instanceof Error ? e.message : '示唆を作れませんでした')
    }
  })

  const items = data?.items ?? []

  return {
    items,
    current: items.find((x) => x.id === selected) ?? items[0],
    keepGenerations: data?.keep_generations ?? 10,
    isLoading,
    isMutating,
    step,
    error,
    days,
    setSelected,
    run: () => {
      setError(null)
      setStep(1)
      window.setTimeout(() => setStep(2), STEP2_AFTER_MS)
      trigger({ site_id: siteId, days })
    }
  }
}

export type PersonaInsightState = ReturnType<typeof usePersonaInsight>
